import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  Button,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Alert,
  Snackbar,
  Badge,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Paper,
  Tooltip,
  CircularProgress,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  ListItemAvatar,
  ListItemSecondaryAction,
  AppBar,
  Toolbar,
  Collapse,
  LinearProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Delete as DeleteIcon,
  MarkEmailRead as MarkReadIcon,
  Settings as SettingsIcon,
  MoreVert as MoreVertIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  MonetizationOn as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrackChanges as TargetIcon,
  Receipt as TransactionIcon,
  AccountBalance as BudgetIcon,
  Timeline as GoalIcon,
  Schedule as ReminderIcon,
  Security as SecurityIcon,
  ClearAll as ClearAllIcon,
  NotificationsActive as NotificationsActiveIcon,
  NotificationsOff as NotificationsOffIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow, isToday, isThisWeek, startOfDay, startOfWeek } from 'date-fns';
import { 
  fetchNotifications, 
  markAsRead, 
  removeNotification, 
  markAllAsRead, 
  deleteAllRead 
} from '../store/slices/notificationSlice';

const notificationTypes = {
  transaction: { icon: <TransactionIcon />, color: '#2196f3', label: 'Transaction' },
  budget: { icon: <BudgetIcon />, color: '#ff9800', label: 'Budget' },
  goal: { icon: <GoalIcon />, color: '#4caf50', label: 'Goal' },
  reminder: { icon: <ReminderIcon />, color: '#9c27b0', label: 'Reminder' },
  security: { icon: <SecurityIcon />, color: '#f44336', label: 'Security' },
  system: { icon: <InfoIcon />, color: '#607d8b', label: 'System' },
};

const Notifications = () => {
  const dispatch = useDispatch();
  const { notifications, loading, error } = useSelector(state => state.notifications);
  
  const [tabValue, setTabValue] = useState(0);
  const [filter, setFilter] = useState('all');
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedGroups, setExpandedGroups] = useState({ today: true, thisWeek: true, older: false });
  const [notificationSettings, setNotificationSettings] = useState({
    transactionAlerts: true,
    budgetWarnings: true,
    goalReminders: true,
    weeklyReports: true,
    securityAlerts: true,
    emailNotifications: false,
    pushNotifications: true,
    soundEnabled: true,
  });

  // Get notifications from Redux store (backend-driven)
  const allNotifications = notifications || [];

  useEffect(() => {
    dispatch(fetchNotifications());
  }, [dispatch]);

  const handleMarkAsRead = async (notificationId) => {
    await dispatch(markAsRead(notificationId));
    showSnackbar('Notification marked as read', 'success');
  };

  const handleDelete = async (notificationId) => {
    await dispatch(removeNotification(notificationId));
    showSnackbar('Notification deleted', 'success');
    setAnchorEl(null);
  };

  const handleMarkAllAsRead = async () => {
    await dispatch(markAllAsRead());
    showSnackbar('All notifications marked as read', 'success');
  };

  const handleDeleteAllRead = async () => {
    await dispatch(deleteAllRead());
    showSnackbar('All read notifications deleted', 'success');
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getFilteredNotifications = () => {
    let filtered = allNotifications;

    // Filter by tab
    if (tabValue === 1) {
      filtered = filtered.filter(n => !n.is_read);
          } else if (tabValue === 2) {
        filtered = filtered.filter(n => n.is_read);
    }

    // Filter by type
    if (filter !== 'all') {
      filtered = filtered.filter(n => n.type === filter);
    }

    return filtered;
  };

  const groupNotificationsByDate = (notifications) => {
    const today = startOfDay(new Date());
    const thisWeek = startOfWeek(new Date());

    const groups = {
      today: [],
      thisWeek: [],
      older: [],
    };

    notifications.forEach(notification => {
      const notificationDate = new Date(notification.created_at);
      
      if (notificationDate >= today) {
        groups.today.push(notification);
      } else if (notificationDate >= thisWeek) {
        groups.thisWeek.push(notification);
      } else {
        groups.older.push(notification);
      }
    });

    return groups;
  };

  const getNotificationIcon = (type, priority) => {
    const typeInfo = notificationTypes[type] || notificationTypes.system;
    return (
      <Avatar sx={{ 
        bgcolor: typeInfo.color, 
        width: 40, 
        height: 40,
        border: priority === 'high' ? '2px solid #f44336' : 'none',
      }}>
        {typeInfo.icon}
      </Avatar>
    );
  };

  const getPriorityChip = (priority) => {
    const colors = {
      high: 'error',
      medium: 'warning',
      low: 'info',
    };

    return (
      <Chip
        label={priority.toUpperCase()}
        color={colors[priority]}
        size="small"
        variant="outlined"
      />
    );
  };

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const filteredNotifications = getFilteredNotifications();
  const groupedNotifications = groupNotificationsByDate(filteredNotifications);
      const unreadCount = allNotifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsActiveIcon color="primary" />
            </Badge>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Mark All as Read">
            <IconButton onClick={handleMarkAllAsRead} disabled={unreadCount === 0}>
              <DoneAllIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear Read Notifications">
            <IconButton onClick={handleDeleteAllRead}>
              <ClearAllIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Notification Settings">
            <IconButton onClick={() => setSettingsDialog(true)}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold' }}>
                {allNotifications.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Notifications
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="error.main" sx={{ fontWeight: 'bold' }}>
                {unreadCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unread
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="success.main" sx={{ fontWeight: 'bold' }}>
                {allNotifications.filter(n => n.priority === 'high').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                High Priority
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs and Filters */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="All" />
            <Tab label={`Unread (${unreadCount})`} />
            <Tab label="Read" />
          </Tabs>
        </Box>
        
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <FilterIcon color="action" />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filter by Type</InputLabel>
            <Select
              value={filter}
              label="Filter by Type"
              onChange={(e) => setFilter(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              {Object.entries(notificationTypes).map(([key, type]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {type.icon}
                    {type.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Card>

      {/* Notifications List */}
      <Card>
        <CardContent>
          {filteredNotifications.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <NotificationsOffIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No notifications found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tabValue === 1 ? "You're all caught up!" : "No notifications match your current filter."}
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Today */}
              {groupedNotifications.today.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    onClick={() => toggleGroup('today')}
                    startIcon={expandedGroups.today ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Today ({groupedNotifications.today.length})
                  </Button>
                  <Collapse in={expandedGroups.today}>
                    <List>
                      {groupedNotifications.today.map((notification) => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onMarkAsRead={handleMarkAsRead}
                          onDelete={handleDelete}
                          onMenuOpen={(event, notif) => {
                            setAnchorEl(event.currentTarget);
                            setSelectedNotification(notif);
                          }}
                        />
                      ))}
                    </List>
                  </Collapse>
                </Box>
              )}

              {/* This Week */}
              {groupedNotifications.thisWeek.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    onClick={() => toggleGroup('thisWeek')}
                    startIcon={expandedGroups.thisWeek ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    This Week ({groupedNotifications.thisWeek.length})
                  </Button>
                  <Collapse in={expandedGroups.thisWeek}>
                    <List>
                      {groupedNotifications.thisWeek.map((notification) => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onMarkAsRead={handleMarkAsRead}
                          onDelete={handleDelete}
                          onMenuOpen={(event, notif) => {
                            setAnchorEl(event.currentTarget);
                            setSelectedNotification(notif);
                          }}
                        />
                      ))}
                    </List>
                  </Collapse>
                </Box>
              )}

              {/* Older */}
              {groupedNotifications.older.length > 0 && (
                <Box>
                  <Button
                    onClick={() => toggleGroup('older')}
                    startIcon={expandedGroups.older ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Older ({groupedNotifications.older.length})
                  </Button>
                  <Collapse in={expandedGroups.older}>
                    <List>
                      {groupedNotifications.older.map((notification) => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onMarkAsRead={handleMarkAsRead}
                          onDelete={handleDelete}
                          onMenuOpen={(event, notif) => {
                            setAnchorEl(event.currentTarget);
                            setSelectedNotification(notif);
                          }}
                        />
                      ))}
                    </List>
                  </Collapse>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
                        {selectedNotification && !selectedNotification.is_read && (
          <MenuItem onClick={() => handleMarkAsRead(selectedNotification.id)}>
            <MarkReadIcon sx={{ mr: 1 }} />
            Mark as Read
          </MenuItem>
        )}
        <MenuItem onClick={() => handleDelete(selectedNotification?.id)} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Settings Dialog */}
      <Dialog open={settingsDialog} onClose={() => setSettingsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Notification Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Alert Types</Typography>
            {Object.entries(notificationSettings).slice(0, 5).map(([key, value]) => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    checked={value}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      [key]: e.target.checked
                    })}
                  />
                }
                label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                sx={{ display: 'block', mb: 1 }}
              />
            ))}
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" sx={{ mb: 2 }}>Delivery Preferences</Typography>
            {Object.entries(notificationSettings).slice(5).map(([key, value]) => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    checked={value}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      [key]: e.target.checked
                    })}
                  />
                }
                label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                sx={{ display: 'block', mb: 1 }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => {
            setSettingsDialog(false);
            showSnackbar('Settings saved successfully', 'success');
          }}>
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Notification Item Component
const NotificationItem = ({ notification, onMarkAsRead, onDelete, onMenuOpen }) => {
  const typeInfo = notificationTypes[notification.type] || notificationTypes.system;

  return (
    <ListItem
      sx={{
        borderRadius: 2,
        mb: 1,
        backgroundColor: notification.is_read ? 'background.paper' : 'action.hover',
        opacity: notification.is_read ? 0.8 : 1,
        border: '1px solid',
        borderColor: notification.is_read ? 'divider' : typeInfo.color,
        borderLeftWidth: 4,
        '&:hover': {
          backgroundColor: 'action.selected',
        },
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: typeInfo.color, width: 40, height: 40 }}>
          {typeInfo.icon}
        </Avatar>
      </ListItemAvatar>
      
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: notification.is_read ? 400 : 600 }}>
              {notification.title}
            </Typography>
            <Chip
              label={notification.priority.toUpperCase()}
              color={notification.priority === 'high' ? 'error' : notification.priority === 'medium' ? 'warning' : 'info'}
              size="small"
              variant="outlined"
            />
            {!notification.is_read && (
              <Chip label="NEW" color="primary" size="small" />
            )}
          </Box>
        }
        secondary={
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {notification.message}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
            </Typography>
            {notification.action && (
              <Button
                size="small"
                variant="text"
                sx={{ ml: 1, textTransform: 'none' }}
              >
                {notification.action.label}
              </Button>
            )}
          </Box>
        }
      />
      
      <ListItemSecondaryAction>
        <IconButton
          onClick={(e) => onMenuOpen(e, notification)}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

export default Notifications; 